import React, { useState, useEffect, useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import Sidebar from './Sidebar';
import { Menu, X } from 'lucide-react';
import BottomNav from './BottomNav';
import { useCountryCheck } from '../hooks/useCountryCheck';

export default function Layout() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Check if user needs country setup
  useCountryCheck();

  // Handle escape key to close sidebar
  const handleEscapeKey = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape' && isSidebarOpen) {
      setIsSidebarOpen(false);
    }
  }, [isSidebarOpen]);

  // Control body scroll and keyboard events when menu is open
  useEffect(() => {
    if (isSidebarOpen) {
      document.body.classList.add('menu-open');
      window.addEventListener('keydown', handleEscapeKey);
    } else {
      document.body.classList.remove('menu-open');
    }

    return () => {
      document.body.classList.remove('menu-open');
      window.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isSidebarOpen, handleEscapeKey]);

  // Close sidebar on resize if screen becomes large
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024 && isSidebarOpen) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isSidebarOpen]);

  const toggleSidebar = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event from bubbling
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = useCallback((e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation(); // Prevent event from bubbling
      // Only close if clicking the overlay directly
      if (e.target !== e.currentTarget) return;
    }
    setIsSidebarOpen(false);
  }, []);

  return (
    <div className="min-h-screen bg-black flex relative">
      {/* Mobile Menu Button - Fixed position */}
      <button
        onClick={toggleSidebar}
        className="lg:hidden fixed top-4 left-4 z-[60] p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
        aria-label={isSidebarOpen ? 'Close menu' : 'Open menu'}
      >
        {isSidebarOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <Menu className="w-6 h-6 text-white" />
        )}
      </button>

      {/* Mobile Overlay - above CardModal (z-50) */}
      {isSidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-[55] backdrop-blur-sm"
          onClick={closeSidebar}
          aria-hidden="true"
        />
      )}

      {/* Sidebar Container - above CardModal (z-50) */}
      <div 
        className={`
          fixed inset-y-0 left-0 z-[56] w-64
          transform transition-transform duration-300 ease-out
          lg:transform-none lg:static lg:z-[1]
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        onClick={(e) => e.stopPropagation()} // Prevent clicks from reaching overlay
      >
        <Sidebar isOpen={isSidebarOpen} onClose={closeSidebar} />
      </div>

      {/* Main Content */}
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
      {/* Bottom Navigation for Mobile */}
      <BottomNav onMenuClick={() => setIsSidebarOpen(true)} />
    </div>
  );
}